import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { describePep, PEP_NOT_STATED, PepFunction, pepFunctionLabel, PepStatus, pepStatusLabel, } from '@kanos/domain';
import { DateField, FieldGroup, SegmentedControl, SelectField, TextField } from '@kanos/ui';
import { useState } from 'react';
import { CountryField } from './country-field.js';
import { todayIso } from './form.js';
import { formatDate } from './texts.js';
/** The answers in a word or two each, as a switch; the question stands under it. */
const SHORT = {
    you: { none: 'Nein', pep: 'Ja, ich', family_member: 'Familie', close_associate: 'Nahestehend' },
    person: {
        none: 'Nein',
        pep: 'Ja, selbst',
        family_member: 'Familie',
        close_associate: 'Nahestehend',
    },
};
const functions = PepFunction.options.map((id) => ({ id, label: pepFunctionLabel[id] }));
const holdsOffice = (status) => status !== null && status !== 'none';
const related = (status) => status === 'family_member' || status === 'close_associate';
/** The statement in one line, with the app's dates (`describePep`). */
export const pepText = (pep) => describePep(pep, formatDate);
/**
 * Whether a person is politically exposed (§ 1 Abs. 12-14 GwG, ADR-029), for the firm app and
 * the portal alike: the answer, and with it which office, where and until when - for a family
 * member or a close associate also of whom. Nothing answered stays "not stated".
 */
export function usePepFields(pep, 
/** Whom the question is put to: the person itself (`you`, in the portal), or about a person. */
asked, 
/** Its number as a section of a longer form (`FieldGroup`). */
step) {
    const saved = pep ?? PEP_NOT_STATED;
    const [status, setStatus] = useState(saved.status);
    const [fn, setFn] = useState(saved.function);
    const [office, setOffice] = useState(saved.office ?? '');
    const [country, setCountry] = useState(saved.country ?? '');
    const [endedOn, setEndedOn] = useState(saved.endedOn);
    const [relatedTo, setRelatedTo] = useState(saved.relatedTo ?? '');
    const values = holdsOffice(status)
        ? {
            status,
            function: fn,
            office: office.trim() || null,
            country: country || null,
            endedOn,
            relatedTo: related(status) ? relatedTo.trim() || null : null,
        }
        : { ...PEP_NOT_STATED, status };
    // The question in one line under the group; what counts as an office behind its „?“.
    const fields = (_jsxs(FieldGroup, { title: "Politisch exponierte Person", ...(step === undefined ? {} : { step }), note: asked === 'you'
            ? 'Haben oder hatten Sie ein wichtiges öffentliches Amt?'
            : 'Hat oder hatte die Person ein wichtiges öffentliches Amt?', help: _jsx(_Fragment, { children: "Etwa in Regierung, Parlament, Parteivorstand, hohem Gericht, Zentralbank, Botschaft oder Leitung eines staatseigenen Unternehmens. Gefragt ist auch nach nahen Familienangeh\u00F6rigen und bekannten engen Vertrauten solcher Personen. Das Geldw\u00E4schegesetz verlangt diese Angabe." }), children: [_jsx(SegmentedControl, { "aria-label": "Politisch exponiert", options: PepStatus.options.map((id) => ({ id, label: SHORT[asked][id] })), value: status, onChange: setStatus, appearance: "field", className: "w-full [&>*]:flex-1" }), status !== null && status !== 'none' && (_jsx("p", { className: "-mt-2 px-1 text-sm text-muted", children: pepStatusLabel[status] })), holdsOffice(status) && (_jsxs(_Fragment, { children: [_jsx(SelectField, { label: "Art des Amts", placeholder: "Bitte ausw\u00E4hlen", options: functions, value: fn, onChange: setFn }), _jsx(TextField, { label: "Amt", placeholder: "z. B. Mitglied des Landtags NRW", value: office, onChange: setOffice, maxLength: 200 }), _jsx(CountryField, { label: "Land des Amts", value: country, onChange: setCountry, extra: ['EU'] }), _jsx(DateField, { label: "Amt endete am", description: "Leer, wenn das Amt noch besteht.", value: endedOn, onChange: setEndedOn, maxValue: todayIso() }), related(status) && (_jsx(TextField, { label: "Name der PEP", description: "Die politisch exponierte Person, zu der die Beziehung besteht.", placeholder: "z. B. Erika Musterfrau", value: relatedTo, onChange: setRelatedTo, maxLength: 200 }))] }))] }));
    return { values, fields };
}
//# sourceMappingURL=pep-fields.js.map